import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import { useGT } from 'gt-react';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  RespondToMockUiInput,
  SetMockUiConfigInput,
  SetMockUiContractInput,
  StartMockUiInput,
  SubmitInterviewTakeoverInput,
} from '@erp/rpc';
import { createLaunchRequestTracker } from '../launchRequestIdentity';

/**
 * RPC hooks for the Mock UI Studio flow (AI-340).
 *
 * `startMockUiStudio` returns a `{ sessionId, agentRunId }` the caller feeds into the shared
 * operator stream (`useOperatorSessionStream`). The agent's request_feedback pause is answered
 * via `respondToMockUi` (text + optional screenshot data URIs) — the same park/resume loop the
 * interview uses, minus the Kernel browser.
 */

export function useStartMockUi(options?: {
  onSuccess?: (data: { sessionId: string; agentRunId: string }) => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: StartMockUiInput) =>
      rpcClient.agent.startMockUiStudio(input),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to start mock UI build',
        color: 'error',
      });
    },
  });
}

/** The mock UIs available to seed for a "modify existing" build (org-authed list). */
export function useMockUiSites(organizationId: string | undefined) {
  const query = useQuery({
    ...orpc.agent.listMockUiSites.queryOptions({
      input: organizationId ? { organizationId } : skipToken,
    }),
    enabled: Boolean(organizationId),
  });
  return { ...query, sites: query.data?.sites ?? [] };
}

export function useStartMockUiExplore(options?: {
  onSuccess?: (data: { sessionToken: string; liveViewUrl: string }) => void;
}) {
  const { showToast } = useToast();
  const gt = useGT();
  return useMutation({
    mutationFn: (input: { organizationId: string; site: string }) =>
      rpcClient.agent.startMockUiExplore(input),
    onSuccess: options?.onSuccess,
    onError: (error: Error) => {
      showToast({
        title: gt('Error'),
        message:
          error.message ||
          gt('Failed to open the mock UI', {
            context: 'error shown when a mock UI browser cannot be started',
          }),
        color: 'error',
      });
    },
  });
}

export function useCloseMockUiExplore() {
  return useMutation({
    mutationFn: (input: { organizationId: string; sessionToken: string }) =>
      rpcClient.agent.closeMockUiExplore(input),
  });
}

/** Seed a reusable eval suite from the built mock and trigger a rubric-graded operator run against
 * its live preview. Returns the new suite id; the run appears in Agent Evals. */
export function useRunMockUiEval(options?: {
  onSuccess?: (data: { suiteId: string }) => void;
}) {
  const { showToast } = useToast();
  const [launch] = useState(createLaunchRequestTracker);

  return useMutation({
    mutationFn: (
      input: Omit<
        Parameters<typeof rpcClient.core.agentEvals.runMockUiEval>[0],
        'requestId'
      >,
    ) =>
      launch(input, (requestId) =>
        rpcClient.core.agentEvals.runMockUiEval({ ...input, requestId }),
      ),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to start the eval',
        color: 'error',
      });
    },
  });
}

/** The SOP-projected state machine of a mock's latest published build (AI-340) — for the
 * no-live-session view (revisiting a published mock). Returns null until a build is published. */
export function useMockUiStateGraph(
  organizationId: string | undefined,
  site: string | null | undefined,
) {
  const query = useQuery({
    ...orpc.agent.getMockUiStateGraph.queryOptions({
      input: organizationId && site ? { organizationId, site } : skipToken,
    }),
    enabled: Boolean(organizationId && site),
  });
  return { ...query, graph: query.data?.graph ?? null };
}

/** The published version history of a mock (AI-340) — each build is an immutable version with an
 * AI-generated changelog + the AgentRun that built it. Newest first; `currentVersion` is the highest
 * published pointer (null until the mock has ever been published). */
export function useMockUiVersions(
  organizationId: string | undefined,
  site: string | null | undefined,
) {
  const query = useQuery({
    ...orpc.agent.getMockUiVersions.queryOptions({
      input: organizationId && site ? { organizationId, site } : skipToken,
    }),
    enabled: Boolean(organizationId && site),
  });
  return {
    ...query,
    currentVersion: query.data?.currentVersion ?? null,
    versions: query.data?.versions ?? [],
    config: query.data?.config ?? {
      autoReEvalOnChange: false,
      flagRealityStale: false,
      realBaseUrl: null,
      realCredentialSetId: null,
    },
    reality: query.data?.reality ?? null,
    evalSuiteId: query.data?.evalSuiteId ?? null,
  };
}

/** Set a mock's opt-in automation toggles (AI-340: auto re-eval on change / reality-stale flag),
 * refreshing the version panel on success. */
export function useSetMockUiConfig(
  organizationId: string | undefined,
  site: string | null | undefined,
) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetMockUiConfigInput) =>
      rpcClient.agent.setMockUiConfig(input),
    onSuccess: () => {
      if (organizationId && site) {
        void queryClient.invalidateQueries({
          queryKey: orpc.agent.getMockUiVersions.queryOptions({
            input: { organizationId, site },
          }).queryKey,
        });
      }
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update mock automation',
        color: 'error',
      });
    },
  });
}

/** Save a mock's human-authored contract overlay (per-state precondition/prohibitions + mock-level
 * parameters), refreshing the state graph so the merged (human-wins) values re-render. */
export function useSetMockUiContract(
  organizationId: string | undefined,
  site: string | null | undefined,
) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetMockUiContractInput) =>
      rpcClient.agent.setMockUiContract(input),
    onSuccess: () => {
      if (organizationId && site) {
        void queryClient.invalidateQueries({
          queryKey: orpc.agent.getMockUiStateGraph.queryOptions({
            input: { organizationId, site },
          }).queryKey,
        });
      }
      showToast({
        title: 'Saved',
        message: 'Contract updated',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to save the contract',
        color: 'error',
      });
    },
  });
}

/** The SOPs taught against a mock, keyed by `?case=` (AI-340) — drives the per-case teach/golden
 * status, the deterministic-eval gate, and the state-graph SOP overlay. */
export function useMockUiTaughtSops(hostedUrl: string | null | undefined) {
  const query = useQuery({
    ...orpc.core.agentEvals.listMockUiTaughtSops.queryOptions({
      input: hostedUrl ? { hostedUrl } : skipToken,
    }),
    enabled: Boolean(hostedUrl),
  });
  return { ...query, taught: query.data?.sops ?? [] };
}

/** Run the DETERMINISTIC procedure eval over the mock's taught SOPs — replays each SOP and grades
 * its captured `window.__evalState` against the SOP-version golden (no LLM). Returns the suite id. */
export function useRunMockUiProcedureEval(options?: {
  onSuccess?: (data: { suiteId: string }) => void;
}) {
  const { showToast } = useToast();
  const [launch] = useState(createLaunchRequestTracker);

  return useMutation({
    mutationFn: (
      input: Omit<
        Parameters<typeof rpcClient.core.agentEvals.runMockUiProcedureEval>[0],
        'requestId'
      >,
    ) =>
      launch(input, (requestId) =>
        rpcClient.core.agentEvals.runMockUiProcedureEval({
          ...input,
          requestId,
        }),
      ),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to start the deterministic eval',
        color: 'error',
      });
    },
  });
}

export function useRespondMockUi(options?: { onSuccess?: () => void }) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: RespondToMockUiInput) =>
      rpcClient.agent.respondToMockUi(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to send feedback',
        color: 'error',
      });
    },
  });
}

/** Finish a confirm_action pause: tell the agent to record what the user just did in the interactive
 * preview (drains the captured gestures + resulting window.__evalState). AI-340. */
export function useRespondMockUiTakeover(options?: { onSuccess?: () => void }) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: SubmitInterviewTakeoverInput) =>
      rpcClient.agent.respondToMockUiTakeover(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to record the action',
        color: 'error',
      });
    },
  });
}
